<template>
  <div class="relative">
    <!-- 版本号显示按钮 -->
    <button
      type="button"
      @click="showVersionInfo"
      class="text-xs text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-x-1"
    >
      <TagIcon class="h-3.5 w-3.5" />
      <span>{{ versionInfo?.version || "0.0.0" }}</span>
    </button>

    <!-- 版本信息弹窗 -->
    <TransitionRoot as="template" :show="isModalOpen">
      <Dialog as="div" class="relative z-50" @close="closeModal">
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div
            class="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity"
          />
        </TransitionChild>

        <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div
            class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
          >
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 sm:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                class="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-xl transition-all w-full sm:my-8 sm:w-full sm:max-w-sm mx-auto"
              >
                <!-- 主要内容区域 -->
                <div class="bg-white px-6 pt-6 pb-4">
                  <!-- 标题和关闭按钮 -->
                  <div class="flex items-start justify-between mb-6">
                    <div class="flex items-center gap-x-3">
                      <div
                        class="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100"
                      >
                        <TagIcon class="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 class="text-lg font-semibold text-gray-900">
                          {{ t("version.title") }}
                        </h3>
                        <p class="text-sm text-gray-500">
                          {{ systemConfig }}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      class="rounded-lg p-1 text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors"
                      @click="closeModal"
                    >
                      <XMarkIcon class="h-5 w-5" />
                    </button>
                  </div>

                  <!-- 版本信息卡片 -->
                  <div class="space-y-4">
                    <!-- 版本号 -->
                    <div class="bg-gray-50 rounded-xl p-4">
                      <div class="flex items-center justify-between">
                        <div class="flex items-center gap-x-3">
                          <div class="p-2 bg-blue-100 rounded-lg">
                            <CodeBracketIcon class="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <p class="text-sm font-medium text-gray-900">
                              {{ t("version.currentVersion") }}
                            </p>
                            <p class="text-xs text-gray-500">
                              {{ t("version.currentVersionDesc") }}
                            </p>
                          </div>
                        </div>
                        <span
                          class="inline-flex items-center rounded-lg bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700 ring-1 ring-inset ring-blue-700/10"
                        >
                          {{ versionInfo?.version || t("version.unknown") }}
                        </span>
                      </div>
                    </div>

                    <!-- 构建时间 -->
                    <div class="bg-gray-50 rounded-xl p-4">
                      <div class="flex items-center justify-between">
                        <div class="flex items-center gap-x-3">
                          <div class="p-2 bg-green-100 rounded-lg">
                            <ClockIcon class="h-4 w-4 text-green-600" />
                          </div>
                          <div>
                            <p class="text-sm font-medium text-gray-900">
                              {{ t("version.buildTime") }}
                            </p>
                            <p class="text-xs text-gray-500">
                              {{ t("version.buildTimeDesc") }}
                            </p>
                          </div>
                        </div>
                        <span class="text-sm text-gray-700 font-mono">
                          {{ formatDate(versionInfo?.buildTime) }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 底部操作区域 -->
                <div class="bg-gray-50 px-6 py-4 rounded-b-2xl">
                  <!-- 关闭按钮 -->
                  <button
                    type="button"
                    class="w-full flex items-center justify-center gap-x-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 transition-colors"
                    @click="closeModal"
                  >
                    {{ t("version.close") }}
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import {
  TagIcon,
  XMarkIcon,
  ClockIcon,
  CodeBracketIcon,
} from "@heroicons/vue/24/outline";
import { useI18n } from "vue-i18n";

interface SimpleVersionInfo {
  name: string;
  version: string;
  buildTime: string;
}

const { t } = useI18n();
const isModalOpen = ref(false);
const versionInfo = ref<SimpleVersionInfo | null>(null);
const systemConfig = import.meta.env.VITE_SYSTEM_CONFIG || "default";

const showVersionInfo = () => {
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
};

const formatDate = (dateString?: string) => {
  if (!dateString) return t("version.unknown");
  return new Date(dateString).toLocaleString();
};

const loadVersionInfo = async () => {
  try {
    // 尝试多个可能的路径
    const possiblePaths = [
      `${import.meta.env.VITE_BASE_URL || "/"}version.json`.replace(
        /\/+/g,
        "/",
      ),
      "/version.json",
      "./version.json",
    ];

    let versionData = null;

    for (const versionUrl of possiblePaths) {
      try {
        console.log("Trying to load version from:", versionUrl);

        const response = await fetch(versionUrl, {
          cache: "no-cache",
          headers: {
            "Cache-Control": "no-cache",
          },
        });

        if (response.ok) {
          versionData = await response.json();
          console.log(
            "Version data loaded successfully from:",
            versionUrl,
            versionData,
          );
          break;
        } else {
          console.warn(
            `Failed to fetch from ${versionUrl}:`,
            response.status,
            response.statusText,
          );
        }
      } catch (pathError) {
        console.warn(`Error fetching from ${versionUrl}:`, pathError);
      }
    }

    if (versionData) {
      versionInfo.value = versionData;
    } else {
      console.warn("Could not load version info from any path");
    }
  } catch (error) {
    console.warn("Failed to load version info:", error);
  }
};

onMounted(() => {
  loadVersionInfo();
});
</script>
